import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Folder, File, Plus, Search, Trash2, Edit, Eye, Upload } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

export default function Documents() {
  const { toast } = useToast();

  const folders = [
    { name: "Meeting Minutes", owner: "Admin", permissions: "Edit", files: 12 },
    { name: "Financial Reports", owner: "Treasurer", permissions: "Edit", files: 8 },
    { name: "Event Planning", owner: "Events Team", permissions: "Edit", files: 15 },
    { name: "Templates", owner: "Admin", permissions: "Read Only", files: 6 },
  ];

  const recentFiles = [
    { name: "AGM Minutes June 2025.pdf", folder: "Meeting Minutes", modified: "2 days ago", size: "245 KB" },
    { name: "Summer Fair Budget.xlsx", folder: "Financial Reports", modified: "1 week ago", size: "89 KB" },
    { name: "Event Checklist Template.docx", folder: "Templates", modified: "2 weeks ago", size: "42 KB" },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Documents</h1>
        <p className="text-muted-foreground mt-2">
          Manage PTA documents with Google Drive and OneDrive integration
        </p>
      </div>

      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Cloud Storage</CardTitle>
              <CardDescription>Connect your cloud storage provider</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline">
                Connect Google Drive
              </Button>
              <Button variant="outline">
                Connect OneDrive
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Cloud Integration:</strong> Connect your Google Drive or OneDrive to sync documents. 
              Admins can create root folders, while other members can create subfolders.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Folders</CardTitle>
              <CardDescription>Organized document storage</CardDescription>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Folder
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Folder</DialogTitle>
                  <DialogDescription>
                    Root folders can only be created by Admins
                  </DialogDescription>
                </DialogHeader>
                <form className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="folder-name">Folder Name</Label>
                    <Input id="folder-name" placeholder="e.g., Event Photos" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="folder-permissions">Default Permissions</Label>
                    <Select>
                      <SelectTrigger id="folder-permissions">
                        <SelectValue placeholder="Select permissions" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="edit">Edit</SelectItem>
                        <SelectItem value="readonly">Read Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="folder-parent">Parent Folder (Optional)</Label>
                    <Select>
                      <SelectTrigger id="folder-parent">
                        <SelectValue placeholder="Root folder" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="root">Root</SelectItem>
                        <SelectItem value="meeting">Meeting Minutes</SelectItem>
                        <SelectItem value="financial">Financial Reports</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline">Cancel</Button>
                    <Button type="submit">Create Folder</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {folders.map((folder, i) => (
              <div key={i} className="p-4 border border-border rounded-lg hover:shadow-card transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <Folder className="h-8 w-8 text-primary" />
                    <div>
                      <h4 className="font-semibold">{folder.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {folder.files} files • Owner: {folder.owner}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant={folder.permissions === "Edit" ? "default" : "secondary"}>
                      {folder.permissions}
                    </Badge>
                    <div className="flex gap-1">
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4" />
                      </Button>
                      {folder.permissions === "Edit" && (
                        <>
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Delete Folder</DialogTitle>
                                <DialogDescription>
                                  Are you sure you want to delete this folder?
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                                  <p className="text-sm text-destructive">
                                    <strong>Warning:</strong> This action cannot be undone. 
                                    All files in this folder will be permanently deleted.
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Button variant="outline" className="flex-1">Cancel</Button>
                                  <Button variant="destructive" className="flex-1" onClick={() => {
                                    toast({
                                      title: "Folder deleted",
                                      description: "The folder has been permanently removed",
                                    });
                                  }}>
                                    Delete Folder
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Recent Files</CardTitle>
              <CardDescription>Recently accessed documents</CardDescription>
            </div>
            <Button>
              <Upload className="h-4 w-4 mr-2" />
              Upload File
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-2">
            {recentFiles.map((file, i) => (
              <div key={i} className="p-3 border border-border rounded-lg hover:bg-accent transition-colors flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <File className="h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium text-sm">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {file.folder} • Modified {file.modified} • {file.size}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
